import type { OpenSeaOfferRef } from "@anaconda/core/bid/types"
import type { Nft } from "@anaconda/core/nft/types"
import {
  type OpenSeaOfferFulfillment,
  validateOpenSeaOfferFulfillment,
} from "@anaconda/core/opensea/offer-fulfillment"
import type { Currency, PriceValue } from "@anaconda/core/price/types"
import { formatUnits } from "viem"

export interface OpenSeaAcceptQuote {
  gross: PriceValue
  fees: PriceValue
  sellerReceives: PriceValue
}

/**
 * What the seller is shown before accepting an OpenSea offer.
 *
 * The amounts come from re-validating the fulfilment against the offer on this
 * row — its order hash, this NFT and this wallet — never from the `expected`
 * figures the response carries about itself, so a wrong or tampered response
 * can't show one price and fill another. Throws when the fulfilment doesn't
 * check out.
 */
export function readOpenSeaAcceptQuote({
  fulfillment,
  offer,
  nft,
  fulfiller,
  currency,
}: {
  fulfillment: OpenSeaOfferFulfillment
  offer: OpenSeaOfferRef
  nft: Pick<Nft, "id">
  fulfiller: string
  currency: Currency
}): OpenSeaAcceptQuote {
  const [, contract = "", tokenId = ""] = nft.id.split(":")
  const amounts = validateOpenSeaOfferFulfillment(fulfillment, {
    orderHash: offer.orderHash,
    fulfiller,
    contract,
    tokenId,
  })

  const price = (wei: string): PriceValue => ({
    amount: formatUnits(BigInt(wei), offer.decimals),
    currency,
  })

  return {
    gross: price(amounts.grossAmountWei),
    fees: price(amounts.feesWei),
    sellerReceives: price(amounts.sellerReceivesWei),
  }
}
